import React, { useEffect, useState } from "react";
import { Button, Spin, Result, Collapse, Tag } from "antd";
import {
  AndroidOutlined, DownloadOutlined, ClockCircleOutlined, MobileOutlined,
  CloudDownloadOutlined, SafetyOutlined, CheckCircleFilled,
} from "@ant-design/icons";
import { getLatestApk, listApks } from "../network/api";
import logo from "../assets/ai360-logo-white.svg";

const BRAND = "#2f54eb";
const NAVY_GRADIENT = "linear-gradient(150deg, #0b1f3a 0%, #14346b 55%, #2f54eb 140%)";

const mb = (n) => (n ? `${(n / (1024 * 1024)).toFixed(1)} MB` : "—");
const fmtDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

// Friendly Android release name for a min-SDK API level (e.g. 26 → "8.0").
const ANDROID_BY_SDK = {
  21: "5.0", 22: "5.1", 23: "6.0", 24: "7.0", 25: "7.1", 26: "8.0", 27: "8.1",
  28: "9", 29: "10", 30: "11", 31: "12", 32: "12L", 33: "13", 34: "14", 35: "15",
};
const androidVersion = (sdk) => (sdk != null ? ANDROID_BY_SDK[sdk] || `SDK ${sdk}` : null);

// A human app name: the manifest label when present, otherwise the uploaded
// filename with the version/extension stripped ("Edge360 Field-v-1.0.1.apk" →
// "Edge360 Field"), falling back to the package id.
const appDisplayName = (b) => {
  if (!b) return "Our App";
  if (b.label) return b.label;
  if (b.originalName) {
    const cleaned = b.originalName
      .replace(/\.apk$/i, "")
      .replace(/[-_ ]*v?[-_ ]?\d+(\.\d+)*(\s*\(\d+\))?$/i, "")
      .replace(/[-_]+$/, "")
      .trim();
    if (cleaned) return cleaned;
  }
  return b.package || "Our App";
};

// Public, no-auth landing page for downloading the Android app.
export default function AppDownload() {
  const [latest, setLatest] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [l, list] = await Promise.all([
          getLatestApk().catch((e) => {
            if (e?.response?.status === 404) return null; // nothing published yet
            throw e;
          }),
          listApks().catch(() => []),
        ]);
        if (!alive) return;
        setLatest(l);
        setAll(list);
      } catch {
        if (alive) setError("Something went wrong loading the app. Please try again later.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const shell = (children) => (
    <div style={styles.page}>
      <div style={styles.card}>{children}</div>
      <div style={styles.footer}>© {new Date().getFullYear()} AI360 Retail</div>
    </div>
  );

  if (loading) {
    return shell(
      <div style={{ padding: 72, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return shell(<Result status="warning" title="Couldn't load the app" subTitle={error} style={{ padding: 24 }} />);
  }

  if (!latest) {
    return shell(
      <>
        <Hero name="Android App" version={null} build={null} />
        <div style={{ padding: 24 }}>
          <Result
            icon={<AndroidOutlined style={{ color: "#52c41a" }} />}
            title="No app available yet"
            subTitle="The Android app hasn't been published. Please check back soon."
          />
        </div>
      </>
    );
  }

  const name = appDisplayName(latest);
  const andr = androidVersion(latest.minSdkVersion);
  const older = all.filter((b) => b.filename !== latest.filename);

  return shell(
    <>
      <Hero name={name} version={latest.versionName} build={latest.versionCode} />

      <div style={styles.body}>
        <a href={latest.url} style={{ textDecoration: "none" }}>
          <button style={styles.downloadBtn} type="button">
            <DownloadOutlined style={{ fontSize: 20 }} />
            <span>Download APK</span>
            <span style={styles.downloadSize}>{mb(latest.size)}</span>
          </button>
        </a>
        <div style={styles.subline}>
          <SafetyOutlined /> Direct download{andr ? ` · Android ${andr}+` : ""} · Free
        </div>

        {/* meta grid */}
        <div style={styles.metaGrid}>
          <Meta icon={<CloudDownloadOutlined />} label="Version" value={latest.versionName || "—"} />
          <Meta icon={<MobileOutlined />} label="Android" value={andr ? `${andr}+` : "—"} />
          <Meta icon={<ClockCircleOutlined />} label="Updated" value={fmtDate(latest.createdAt)} />
        </div>

        {/* how to install */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>How to install</div>
          <ol style={styles.steps}>
            <li>Tap <b>Download APK</b> above to save the file.</li>
            <li>Open the downloaded file on your Android device.</li>
            <li>If asked, allow installs from your browser (<i>Settings → Install unknown apps</i>), then tap <b>Install</b>.</li>
          </ol>
        </div>

        {/* what's new */}
        {latest.notes && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>What's new in {latest.versionName || "this version"}</div>
            <div style={styles.notes}>{latest.notes}</div>
          </div>
        )}

        {/* previous versions */}
        {older.length > 0 && (
          <Collapse
            ghost
            style={{ marginTop: 8 }}
            items={[
              {
                key: "prev",
                label: <span style={{ fontWeight: 600 }}>Previous versions ({older.length})</span>,
                children: (
                  <div>
                    {older.map((b) => (
                      <div key={b.filename} style={styles.prevRow}>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            Version {b.versionName || "—"}
                            {b.versionCode != null && (
                              <Tag style={{ marginLeft: 8 }}>build {b.versionCode}</Tag>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: "#8a94a6" }}>{fmtDate(b.createdAt)}</div>
                        </div>
                        <Button size="small" icon={<DownloadOutlined />} href={b.url}>
                          {mb(b.size)}
                        </Button>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>
    </>
  );
}

// Branded gradient hero: logo, app-icon badge, name and version chips.
function Hero({ name, version, build }) {
  return (
    <div style={styles.hero}>
      <img src={logo} alt="AI360" style={styles.logo} />
      <div style={styles.iconBadge}>
        <AndroidOutlined style={{ fontSize: 40, color: "#fff" }} />
      </div>
      <div style={styles.appName}>{name}</div>
      {(version || build != null) && (
        <div style={styles.chips}>
          {version && <span style={styles.chip}>Version {version}</span>}
          {build != null && <span style={styles.chip}>Build {build}</span>}
        </div>
      )}
      <div style={styles.platform}>
        <CheckCircleFilled style={{ color: "#52c41a" }} /> Official Android build
      </div>
    </div>
  );
}

function Meta({ icon, label, value }) {
  return (
    <div style={styles.metaCell}>
      <div style={{ color: BRAND, fontSize: 18 }}>{icon}</div>
      <div style={styles.metaValue}>{value}</div>
      <div style={styles.metaLabel}>{label}</div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    background: "radial-gradient(1200px 600px at 50% -10%, #e8eefc 0%, #f4f6fb 45%, #eef1f7 100%)",
  },
  card: {
    width: "100%",
    maxWidth: 460,
    background: "#fff",
    borderRadius: 22,
    overflow: "hidden",
    boxShadow: "0 24px 70px rgba(11,31,58,0.18)",
  },
  footer: { marginTop: 18, fontSize: 12, color: "#94a0b4" },

  hero: {
    background: NAVY_GRADIENT,
    color: "#fff",
    padding: "30px 28px 34px",
    textAlign: "center",
  },
  logo: { height: 30, maxWidth: "60%", opacity: 0.95, marginBottom: 22 },
  iconBadge: {
    width: 78,
    height: 78,
    margin: "0 auto 16px",
    borderRadius: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(145deg, #3ddc84 0%, #1faf66 100%)",
    boxShadow: "0 10px 24px rgba(0,0,0,0.28)",
  },
  appName: { fontSize: 24, fontWeight: 700, lineHeight: 1.2 },
  chips: { display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 12 },
  chip: {
    fontSize: 12,
    padding: "3px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.22)",
  },
  platform: { marginTop: 14, fontSize: 13, color: "rgba(255,255,255,0.8)" },

  body: { padding: "26px 28px 30px" },
  downloadBtn: {
    width: "100%",
    height: 56,
    border: "none",
    borderRadius: 14,
    cursor: "pointer",
    color: "#fff",
    fontSize: 17,
    fontWeight: 600,
    background: "linear-gradient(135deg, #2f54eb 0%, #1d39c4 100%)",
    boxShadow: "0 12px 26px rgba(47,84,235,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  downloadSize: {
    fontSize: 13,
    fontWeight: 500,
    opacity: 0.85,
    padding: "2px 8px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.18)",
  },
  subline: { marginTop: 12, textAlign: "center", fontSize: 13, color: "#8a94a6" },

  metaGrid: {
    display: "flex",
    marginTop: 24,
    borderRadius: 14,
    border: "1px solid #eef1f6",
    overflow: "hidden",
    background: "#fafbfe",
  },
  metaCell: { flex: 1, padding: "14px 6px", textAlign: "center", borderRight: "1px solid #eef1f6" },
  metaValue: { marginTop: 6, fontWeight: 700, fontSize: 15, color: "#1f2a44" },
  metaLabel: { fontSize: 11, color: "#98a2b3", textTransform: "uppercase", letterSpacing: 0.4 },

  section: { marginTop: 24 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: "#1f2a44", marginBottom: 10 },
  steps: { margin: 0, paddingLeft: 20, color: "#4a5568", fontSize: 14, lineHeight: 1.9 },
  notes: { whiteSpace: "pre-wrap", color: "#4a5568", fontSize: 14, lineHeight: 1.7 },

  prevRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderTop: "1px solid #f0f2f6",
  },
};
